import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import styles from './StatusDropdown.module.css';

/**
 * Generic custom dropdown to replace native <select> elements.
 * Renders menu in a portal so it's never clipped by parent overflow.
 *
 * Props:
 *   options        – array of { value, label, color? } or sections format (see below)
 *   value          – currently selected value
 *   onChange        – (value) => void
 *   placeholder    – text shown when nothing selected (default: "Select...")
 *   disabled       – boolean
 *   compact        – boolean (smaller variant for table cells)
 *   toolbar        – boolean (filter toolbar variant)
 *   fullWidth      – boolean (wrapper takes 100% width)
 *   actions        – array of { label, onClick } for bottom actions (e.g., "+ Add New")
 *   sections       – alternative to options: array of { items: [...options], separator: bool }
 *                    for grouped options with separators between sections
 */
export default function CustomDropdown({
  options = [],
  value,
  onChange,
  placeholder = 'Select...',
  disabled = false,
  compact = false,
  toolbar = false,
  fullWidth = false,
  actions = [],
  sections = null,
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPos, setMenuPos] = useState({ top: -9999, left: -9999, width: 0 });

  const updatePosition = useCallback(() => {
    if (!triggerRef.current || !menuRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const menuHeight = menuRef.current.offsetHeight;
    const spaceBelow = window.innerHeight - rect.bottom - 4;
    const openUpward = spaceBelow < menuHeight && rect.top > menuHeight;
    setMenuPos({
      top: openUpward ? rect.top - menuHeight - 4 : rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  }, []);

  // Two-pass: first render positions off-screen; after paint we can measure and reposition
  const menuCallbackRef = useCallback((node) => {
    menuRef.current = node;
    if (node) updatePosition();
  }, [updatePosition]);

  useEffect(() => {
    if (!open) {
      setMenuPos({ top: -9999, left: -9999, width: 0 });
      return;
    }
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        menuRef.current && !menuRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  // Flatten sections or use options directly
  const allOptions = sections
    ? sections.flatMap(s => s.items || [])
    : options;

  const selected = allOptions.find(o => String(o.value) === String(value));

  const handleSelect = (val) => {
    setOpen(false);
    if (onChange) onChange(val);
  };

  const triggerClass = [
    styles.trigger,
    compact ? styles.triggerCompact : '',
    toolbar ? styles.triggerToolbar : '',
    open ? styles.triggerOpen : '',
  ].filter(Boolean).join(' ');

  const wrapperClass = [
    styles.wrapper,
    fullWidth ? styles.wrapperFull : '',
  ].filter(Boolean).join(' ');

  const renderOption = (opt) => (
    <div
      key={opt.value}
      className={`${styles.item} ${String(opt.value) === String(value) ? styles.itemSelected : ''}`}
      onClick={() => handleSelect(opt.value)}
    >
      {opt.color && <span className={styles.colorDot} style={{ background: opt.color }} />}
      {opt.label}
    </div>
  );

  const menu = open ? ReactDOM.createPortal(
    <div
      ref={menuCallbackRef}
      className={styles.menu}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      style={{
        position: 'fixed',
        top: menuPos.top,
        left: menuPos.left,
        minWidth: menuPos.width,
      }}
    >
      {sections ? (
        sections.map((section, idx) => (
          <React.Fragment key={idx}>
            {idx > 0 && section.separator !== false && <div className={styles.separator} />}
            {(section.items || []).map(renderOption)}
          </React.Fragment>
        ))
      ) : (
        options.map(renderOption)
      )}

      {actions.length > 0 && (
        <>
          <div className={styles.separator} />
          {actions.map((action, idx) => (
            <div
              key={idx}
              className={styles.actionItem}
              onClick={() => { setOpen(false); action.onClick(); }}
            >
              {action.label}
            </div>
          ))}
        </>
      )}
    </div>,
    document.body
  ) : null;

  return (
    <div className={wrapperClass}>
      <button
        type="button"
        ref={triggerRef}
        className={triggerClass}
        onClick={(e) => { e.stopPropagation(); if (!disabled) setOpen(o => !o); }}
        disabled={disabled}
      >
        {selected?.label || placeholder}
      </button>
      {menu}
    </div>
  );
}
